/** biome-ignore-all lint/complexity/noForEach: <explanation> */
import { Analytics } from '@cobliteam/analytics';
import { DeviceGeneralStatus } from '@cobliteam/api-lib/src/__generated__/graphql';
import { cn } from '@cobliteam/colib-ui/cn';
import { getDeviceStatusLabel, type Vehicle } from '@cobliteam/common-types';
import type { TFunction } from '@cobliteam/i18n';
import diacritics from 'diacritics';
import { type Category, type ColorKeys, type EnumCategory, EnumDeviceType } from './types';

const trackGlobalSearchEvents = {
  openSearch: () => {
    Analytics.track('Global Search - Open', {
      source: 'global_search',
    });
  },

  inputFocus: () => {
    Analytics.track('Global Search - Input Focus', {
      source: 'global_search',
    });
  },

  search: (query: string) => {
    Analytics.track('Global Search - Query', {
      source: 'global_search',
      query,
    });
  },

  categoryChange: (category: EnumCategory) => {
    Analytics.track('Global Search - Category Change', {
      source: 'global_search',
      category,
    });
  },

  itemClick: (itemId: string, itemType: EnumCategory, itemName: string) => {
    Analytics.track('Global Search - Item Click', {
      source: 'global_search',
      itemId,
      itemType,
      itemName,
    });
  },

  updateData: () => {
    Analytics.track('Global Search - Update All Data', {
      source: 'global_search',
    });
  },
};

const parseContent = (content: string) => {
  return content.split('\n').map((line, index) => (
    <p
      key={index}
      className={cn('mb-2 text-xs', {
        'font-medium text-xs': line.match(/^\d+\./),
      })}
    >
      {line}
    </p>
  ));
};

const removeDiacritics = (text: string) => {
  return diacritics
    .remove(text)
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .trim();
};

const deviceStatusLabel = (status: DeviceGeneralStatus, t: TFunction) => {
  return status === DeviceGeneralStatus.LosingData ? t('device_status.losing_data', { ns: 'monitoring' }) : getDeviceStatusLabel(status, t);
};

const deviceTypeLabel = (type?: string) => {
  if (!type) return '---';
  return EnumDeviceType[type as keyof typeof EnumDeviceType] || type;
};

const vehicleLabel = (vehicle: Vehicle, { includeAlias = true }: { includeAlias?: boolean } = {}) => {
  return vehicle
    ? `${vehicle.model.brand || vehicle.model.name || vehicle.model.year ? '' : '---'}
      ${vehicle.model.brand ? `${vehicle.model.brand} - ` : ''}
      ${vehicle.model.name ? `${vehicle.model.name} - ` : ''}
      ${vehicle.model.year && vehicle.model.year !== '0' ? `(${vehicle.model.year})` : ''}
      ${includeAlias && vehicle.alias ? `| ${vehicle.alias}` : ''}
      `
    : '---';
};

// Device and geofence hues identify search categories, independently of semantic status colors.
const activeBarColor = (color?: ColorKeys) => {
  if (!color) return 'after:bg-foreground';
  const variants: Record<ColorKeys, string> = {
    gray: 'after:bg-foreground',
    primary: 'after:bg-primary',
    warning: 'after:bg-warning',
    success: 'after:bg-success',
    'accessory-purple': 'after:bg-[#8e4ec6]',
    'accessory-sky': 'after:bg-[#00749e]',
  };
  return variants[color as keyof typeof variants] || 'after:bg-foreground';
};

const cardColorClass = (color?: ColorKeys) => {
  if (!color) return 'bg-background border-border hover:border-border';
  const variants: Record<ColorKeys, string> = {
    primary: 'bg-secondary border-link hover:border-link',
    warning: 'bg-warning/10 border-warning hover:border-warning',
    success: 'bg-success/10 border-success hover:border-success',
    gray: 'bg-background border-border hover:border-border',
    'accessory-sky': 'bg-[#f1fafd] dark:bg-[#111a27] border-[#e1f6fd] dark:border-[#112840] hover:border-[#d1f0fa] dark:hover:border-[#113555]',
    'accessory-purple': 'bg-[#fbf7fe] dark:bg-[#1e1523] border-[#f7edfe] dark:border-[#301c3b] hover:border-[#f2e2fc] dark:hover:border-[#3d224e]',
  };
  return variants[color as keyof typeof variants] || 'bg-background border-border hover:border-border';
};

const cardIconColorClass = (color?: string) => {
  if (!color) return 'border-border';
  const variants: Record<ColorKeys, string> = {
    primary: 'border-link',
    warning: 'border-warning',
    success: 'border-success',
    gray: 'border-border',
    'accessory-sky': 'border-[#a9daed] dark:border-[#1b537b]',
    'accessory-purple': 'border-[#e0c4f4] dark:border-[#54346b]',
  };
  return variants[color as keyof typeof variants] || 'border-border';
};

const iconColorClass = (color?: string) => {
  if (!color) return '!text-muted-foreground';
  const variants: Record<ColorKeys, string> = {
    primary: '!text-link',
    warning: '!text-warning',
    success: '!text-success',
    gray: '!text-muted-foreground',
    'accessory-sky': '!text-[#00749e] dark:!text-[#75c7f0]',
    'accessory-purple': '!text-[#8145b5] dark:!text-[#d19dff]',
  };
  return variants[color as keyof typeof variants] || '!text-muted-foreground';
};

const getCategory = (id: EnumCategory, categories: Category[]) => {
  return categories.find((category) => category.id === id);
};

export {
  activeBarColor,
  cardColorClass,
  cardIconColorClass,
  deviceStatusLabel,
  deviceTypeLabel,
  getCategory,
  iconColorClass,
  parseContent,
  removeDiacritics,
  trackGlobalSearchEvents,
  vehicleLabel,
};
